import { SpendDebitCardPlus } from "graphql/generated";
import { getBadgeColor } from "helpers/status-color";
import { useEffect, useState } from "react";
import { SnapBadge, SnapDropDown, SnapTable } from "suit";
import {
  ACTIVE,
  activeCardActions,
  FROZEN,
  frozenCardActions,
  INACTIVE,
  inactiveActions,
} from "./lite-data";
import { useAppContexts } from "hooks/use-context";
import DataCard from "shared-components/data-card";
import { useLazyQuery } from "@apollo/client";
import { GET_ALL_CUSTOMER_CARDS } from "graphql/queries/organization";
import { SpinnerContainer } from "shared-components/spinner";
import UnitComponentGenerator from "unit/unit-component-generator";
import { SpendPermissions } from "types/roles-permissions";

type DebitCardProps = {
  itemsPerPage?: number;
};

function DebitCards({ itemsPerPage }: DebitCardProps) {
  const { display, navigate } = useAppContexts();
  const [hasMore, setHasMore] = useState(false);
  const [cards, setCards] = useState<SpendDebitCardPlus[]>([]);
  const [getCards, { loading: loadingCards, data: cardData }] = useLazyQuery(
    GET_ALL_CUSTOMER_CARDS,
    { fetchPolicy: "network-only" }
  );
  const [activeCard, setActiveCard] = useState<string>();
  const [actionToDispatch, setActionToDispatch] = useState<string>();

  useEffect(() => {
    if (!cardData && !loadingCards) {
      getCards();
    }
  });

  useEffect(() => {
    if (!loadingCards && cardData?.spendOrganizationAllActiveCustomerCards) {
      const loadedCards = cardData.spendOrganizationAllActiveCustomerCards
        .cards as SpendDebitCardPlus[];
      setCards(loadedCards);
      if (itemsPerPage !== undefined) {
        setHasMore(
          (cardData.spendOrganizationAllActiveCustomerCards.cards?.length ??
            0) > itemsPerPage
        );
      }
    }
  }, [loadingCards, cardData, itemsPerPage]);

  const cardListener = async () => {
    await getCards();
  };

  useEffect(() => {
    window.addEventListener("unitCardStatusChanged", cardListener);
    window.addEventListener("unitCardActivated", cardListener);
    return () => {
      window.removeEventListener("unitCardStatusChanged", cardListener);
      window.removeEventListener("unitCardActivated", cardListener);
    };
  });

  // after actionToDispatch and activeCard have been updated and react rerenders,
  // query the unit white label component and dispatch the action
  useEffect(() => {
    if (actionToDispatch !== undefined && activeCard !== undefined) {
      (async () => {
        const element = document.querySelector(
          `unit-elements-card[card-id="${activeCard}"]`
        );
        if (!element) {
          console.error(
            `Card ${activeCard} could not load unit white-label component`
          );
          return;
        }
        element.dispatchEvent(new CustomEvent("unitRequestRefresh"));
        await new Promise<void>((resolve) => {
          const listener = ((e: CustomEvent) => {
            if ((e as any).detail.cardResult.data.id === activeCard) {
              window.removeEventListener("unitOnLoad", listener);
              setTimeout(resolve, 100);
            }
          }) as EventListener;
          window.addEventListener("unitOnLoad", listener);
        });
        element.dispatchEvent(
          new CustomEvent("unitRequestCardAction", {
            detail: { action: actionToDispatch },
          })
        );
        setActionToDispatch(undefined);
      })();
    }
  }, [actionToDispatch, activeCard]);

  const determineActions = (status: string) => {
    if (status === ACTIVE) {
      return activeCardActions;
    } else if (status === INACTIVE) {
      return inactiveActions;
    } else if (status === FROZEN) {
      return frozenCardActions;
    } else {
      return undefined;
    }
  };

  const getCardStatus = (cardStatus: string) => {
    let status = cardStatus;
    if (status === "ClosedByCustomer") {
      return "Closed";
    } else {
      // add spaces to multi-word statuses
      return status.replace(/([A-Z])/g, " $1").trim();
    }
  };

  const handleModalData = (card: SpendDebitCardPlus, action: string) => {
    if (!card.id) {
      return;
    }
    // update card id for unit component
    setActiveCard(card.id);
    // action to be dispatched in later effect loop
    setActionToDispatch(action);
  };

  const slicedCards = cards.slice(0, itemsPerPage);

  if (loadingCards) {
    return <SpinnerContainer loading={true} />;
  }

  return (
    <>
      <div className="hidden">
        <UnitComponentGenerator
          type="card"
          data={{
            cardId: activeCard,
          }}
          permRequired={[SpendPermissions.groupDebitCardUpdate]}
        />
      </div>
      {display?.isDesktop && (
        <SnapTable>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Org/Group Name</th>
                <th>Last 4</th>
                <th>Status</th>
                <th>{/* Action */}</th>
              </tr>
            </thead>
            <tbody>
              {slicedCards.map((card) => {
                const options = determineActions(card.status ?? "");
                const status = getCardStatus(card.status!);
                return (
                  <tr key={card.id}>
                    <td className="py-2">{card.userName}</td>
                    <td>{card.groupOrOrgName}</td>
                    <td>{card.lastFour}</td>
                    <td>
                      <SnapBadge
                        size="tiny"
                        color={getBadgeColor(card.status ?? "")}
                      >
                        {status}
                      </SnapBadge>
                    </td>
                    <td>
                      {options && (
                        <SnapDropDown
                          minimal
                          options={options}
                          description={`${card.expiration}`}
                          title="Expiration"
                          onSnap-dropdown-item-selected={(e) => {
                            handleModalData(card, e.detail.value);
                          }}
                        />
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </SnapTable>
      )}
      {display?.isMobile &&
        slicedCards.map((card) => {
          const options = determineActions(card.status ?? "");
          const status = getCardStatus(card.status!);
          return (
            <DataCard
              key={card.id}
              title={`${card.userName}`}
              titleExtra={
                <SnapBadge
                  className="ml-4 mt-1"
                  size="tiny"
                  color={getBadgeColor(status ?? "")}
                >
                  {status}
                </SnapBadge>
              }
              kvLeft={[
                {
                  key: "Org/Group Name",
                  value: `${card.groupOrOrgName}`,
                },
                {
                  key: "Last 4",
                  value: `${card.lastFour}`,
                },
              ]}
              kvRight={[]}
              action={options && 1}
              menuItems={options}
              menuTitle={`Expiration: ${card.expiration}`}
              menuClickListener={(action: string) => {
                handleModalData(card, action);
              }}
            />
          );
        })}
      {hasMore && (
        <p
          className="flex justify-end text-blue-600 text-sm cursor-pointer font-semibold"
          onClick={() => {
            navigate("/programs/debit-cards");
          }}
        >
          More
        </p>
      )}
    </>
  );
}

export default DebitCards;
